import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Package, Trash } from "lucide-react";
import { ASSET_MANAGEMENT } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { configBranchLabel, withConfigBranch } from "../../utils/branchSession";

const AssetManagement = () => {
    const { create, update } = useApiMethods(); // Added remove for delete operations
    const [AssetsTypeData, setAssetsTypeData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null); // Track which row to delete
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('master', 'Asset Type', 'SubMenu')

    // form state
    const [formData, setFormData] = useState({
        id: null,
        code: "",
        asset_type_name: "",
        notes: "",
        is_active: true
    });

    // reset form & state
    const resetForm = () => {
        setFormData({ id: null, code: "", asset_type_name: "", notes: "", is_active: true });
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // table columns
    const AssetsTypeColumns = [
        { accessor: "code", Header: "Asset Type Code", sortable: true, filterable: true },
        {
            accessor: "branch_name",
            Header: "Branch",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "asset_type_name",
            Header: "Asset Type Name",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "notes",
            Header: "Description",
            Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                Cell: ({ row }) => {
                    const handleOpenDialog = () => {
                        setEditMode(true);
                        setEditId(row?.code);
                        setFormData({
                            id: row?.id || null,
                            code: row?.code || "",
                            asset_type_name: row?.asset_type_name || "",
                            notes: row?.notes || "",
                            is_active: row?.is_active ?? true,
                        });
                        setOpenDialog(true);
                    };

                    const handleDeleteClick = (row) => {
                        setSelectedRow(row);
                        setIsDeleteConfirmOpen(true);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenDialog}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>

                            {/* Delete Button (only if row is draft) */}
                            {row?.is_draft && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                    onClick={() => handleDeleteClick(row)}
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ] : [])
    ];

    // save as draft
    const handleDraft = async () => {
        try {
            setLoading(true);
            const payload = withConfigBranch({ ...formData, is_draft: true });
            const response = await create(ASSET_MANAGEMENT, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "User group saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            const payload = {
                ...selectedRow,
                draft_delete: true
            }
            // Use remove method for delete operations
            const response = await update(ASSET_MANAGEMENT, payload, false, setLoading);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft deleted successfully");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft");
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setIsDeleteConfirmOpen(false);
        }
    };

    // save or update
    const handleSave = async () => {
        try {
            setLoading(true);
            let payload = withConfigBranch({ ...formData, is_draft: false });
            let response;

            if (editMode && editId) {
                // For update, use the specific endpoint with ID
                response = await update(ASSET_MANAGEMENT, payload);
            } else {
                response = await create(ASSET_MANAGEMENT, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `User group ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} user group`);
            }
        } catch (error) {
            console.error("Error saving user group:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} user group`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard Icon={Package} title={'Asset Management'} subtitle="Track and manage company assets, equipment, and inventory" totalRecords={totalRecords} buttonEnable={!!addPermission} setOpenDialog={setOpenDialog} buttonName={'Add Asset'} />

            <Reactable
                columns={AssetsTypeColumns}
                data={AssetsTypeData}
                setData={setAssetsTypeData}
                setTotalCount={setTotalRecords}
                url={ASSET_MANAGEMENT}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit Asset" : "Add Asset"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="400px"
            >
                <form className="space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Branch: <span className="font-medium text-gray-800 dark:text-gray-100">{configBranchLabel()}</span>
                    </p>
                    {/* Asset Name */}
                    <div className="space-y-2">
                        <label
                            htmlFor="typename"
                            className={cn(modalFieldLabelClass, "flex items-center gap-1")}
                        >
                            Asset Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="typename"
                            value={formData.asset_type_name}
                            onChange={(e) =>
                                setFormData({ ...formData, asset_type_name: e.target.value })
                            }
                            placeholder="Enter Asset name"
                            required
                            disabled={loading}
                            className={cn(
                                "w-full rounded-lg transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Asset Description */}
                    <div className="space-y-2">
                        <label
                            htmlFor="typeDescription"
                            className={modalFieldLabelClass}
                        >
                            Asset Description
                        </label>
                        <Textarea
                            id="typeDescription"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                            placeholder="Enter Asset description"
                            disabled={loading}
                            {...modalDescriptionFieldProps}
                            className={modalTextareaClass}
                        />
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading || !editMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading || !editMode}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Asset"
                description="Are you sure you want to delete this draft group? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default AssetManagement;